import { gql, useQuery } from '@apollo/client';
import React from 'react';
import type {
  EmbedPostContentQuery,
  EmbedPostContentQueryVariables,
  EmbedPublicPostContentQuery,
  EmbedPublicPostContentQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import PostPreviewQuillComponent from '../../post/PostPreviewQuillComponent';
import useIsPublicView from '../../utils/useIsPublicView';
import { parsePostMentionId } from '../utils/matchMention';
import styles from './EmbedPostContent.module.scss';
import { PostDetailPreviewBanner } from '../../post/PostDetailPreview';
import PostDetailPreviewStateBar from '../../post/PostDetailPreviewStateBar';
import { EmbedComponentScaleContainer } from './EmbedComponent';

const CONTENT_MAX_WIDTH = parseInt(styles.contentMaxWidth, 10);

const EMBED_POST_CONTENT_POST_FRAGMENT = gql`
  fragment EmbedPostContent_post on PublicPostFields {
    id
    content
    version
    ... on PublicPost {
      banner {
        original
      }
    }
    ... on Post {
      banner {
        original
      }
    }
    ...PostDetailPreviewStateBar_post
  }
  ${PostDetailPreviewStateBar.fragments.post}
`;

const EMBED_POST_CONTENT_QUERY = gql`
  query embedPostContent($id: ID!) {
    post(id: $id) {
      id
      ...EmbedPostContent_post
    }
  }
  ${EMBED_POST_CONTENT_POST_FRAGMENT}
`;

const EMBED_PUBLIC_POST_CONTENT_QUERY = gql`
  query embedPublicPostContent($id: ID!) {
    publicPost(id: $id) {
      id
      ...EmbedPostContent_post
    }
  }
  ${EMBED_POST_CONTENT_POST_FRAGMENT}
`;

interface EmbedPostContentProps {
  id: string;
}

const EmbedPostContent = ({ id }: EmbedPostContentProps) => {
  const isPublicView = useIsPublicView();
  const { id: postId, anchor } = parsePostMentionId(id);

  const { data } = useQuery<
    EmbedPostContentQuery,
    EmbedPostContentQueryVariables
  >(EMBED_POST_CONTENT_QUERY, {
    variables: { id: postId },
    skip: isPublicView,
  });

  const { data: publicData } = useQuery<
    EmbedPublicPostContentQuery,
    EmbedPublicPostContentQueryVariables
  >(EMBED_PUBLIC_POST_CONTENT_QUERY, {
    variables: { id: postId },
    skip: !isPublicView,
  });

  const post = isPublicView ? publicData?.publicPost : data?.post;
  const content = post?.content;

  return (
    <div className={styles.container}>
      {post?.__typename === 'Post' && (
        <PostDetailPreviewStateBar
          post={post}
          contentMaxWidth={CONTENT_MAX_WIDTH}
          className={styles.stateBar}
        />
      )}

      {post?.banner?.original && (
        <PostDetailPreviewBanner banner={post.banner.original} />
      )}
      {content && (
        <EmbedComponentScaleContainer>
          <PostPreviewQuillComponent
            autoHeight
            content={content}
            anchor={anchor}
          />
        </EmbedComponentScaleContainer>
      )}
    </div>
  );
};

export default EmbedPostContent;
